import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsMenu from "react-notifications-menu";
import blueBellIcon from "./blueBellIcon.svg";
import styles from "./notificationsHandler.module.css";
import ConfirmationModal from "../confirmationModal/ConfirmationModal";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import learningZoneInvitation from "./learningZoneInvitation.png";
import palRequest from "./palRequest.png";

const NotificationHandler = () => {
  const [notifications, setNotifications] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
    const intervalId = setInterval(fetchNotifications, 30000); // Refresh every 30 seconds

    return () => clearInterval(intervalId); // Cleanup interval on component unmount
  }, []);

  /**
   * Get notifications for the user from the database
   */
  const fetchNotifications = async () => {
    try {
      const response = await axios.get("/notifications", {
        withCredentials: true,
      });
      if (response.status === 200 && response.data.success) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  // Filter notifications to only include unread ones
  const unreadNotifications = notifications.filter(
    (notification) => !notification.is_read
  );

  useEffect(() => {
    /**
     * Click event for the notifications pop-up - if a notification is clicked, open the modal to show it, and close the notifications pop-up.
     * @param {*} event
     */
    const handleClick = (event) => {
      const card = event.target.closest(".card");
      if (card) {
        const messageElement = card.querySelector(".message .text");

        if (messageElement) {
          const message = messageElement.textContent;

          const notification = unreadNotifications.find(
            (notif) => notif.message === message
          );
          if (notification) {
            setModalContent(notification);
            setShowModal(true);
            setShowNotifications(false); // Close notifications pop-up
          }
        }
      }
    };

    const container = notificationContainerRef.current;
    if (container) {
      container.addEventListener("click", handleClick);
    }

    return () => {
      if (container) {
        container.removeEventListener("click", handleClick);
      }
    };
  }, [unreadNotifications]);

  /**
   * Handle confirmation of a user to a request displayed in the notification modal. In the case of accepting an invitation to a learning zone, the user will be added to the members list of the learning zone, and will see a toast welcome message. The user will also be redirected to the learning zone page.
   * In the case of confirming a friendship request, the user and the sender will become Pals (friends), and the user will see a toast message that confirms that.
   */
  const handleConfirm = async () => {
    try {
      let response;

      if (modalContent.type === "learningZoneInvite") {
        response = await axios.put(
          `/learning_zones/list/invite/${modalContent.learning_zone_id}/${modalContent.receiver_username}`,
          {
            isAccepted: true,
            notificationId: modalContent.id, // Send the notification ID
          },
          { withCredentials: true }
        );

        if (response.status === 200 && response.data.success) {
          setShowModal(false);
          toast.success(
            `Welcome to the learning zone ${modalContent.learning_zone_name}!`
          );
          navigate(`/learningZone/${modalContent.learning_zone_id}`);
        }
      } else if (modalContent.type === "palRequest") {
        response = await axios.put(
          `/relationships/request/${modalContent.sender_username}/${modalContent.receiver_username}`,
          {
            requestRes: true,
            notificationId: modalContent.id, // Send the notification ID
          },
          { withCredentials: true }
        );

        if (response.status === 200) {
          setShowModal(false);
          toast.success(
            `${modalContent.sender_username} has been added to your Pals!`
          );
        } else {
          console.error(`palRequest Error: ${response.status}`);
        }
      }

      // Mark the notification as read
      setNotifications((prevNotifications) =>
        prevNotifications.map((notif) =>
          notif.id === modalContent.id ? { ...notif, is_read: true } : notif
        )
      );

      // Update the list of notifications, so that the read notification will not displayed anymore.
      fetchNotifications();
    } catch (error) {
      console.error("Error responding to notification:", error);
    }
  };

  /**
   * Handle a negative response from the user to a request displayed in a notification. In the case of a learning zone invitation, the pending invitation will be removed from the database.
   * In the case of a friendship request, the relationship between the two users will be set to null in the database (the friendship request will be deleted).
   */
  const handleCancel = async () => {
    try {
      let response;

      if (modalContent.type === "learningZoneInvite") {
        response = await axios.put(
          `/learning_zones/list/invite/${modalContent.learning_zone_id}/${modalContent.receiver_username}`,
          {
            isAccepted: false,
            notificationId: modalContent.id, // Send the notification ID
          },
          { withCredentials: true }
        );
      } else if (modalContent.type === "palRequest") {
        response = await axios.put(
          `/relationships/request/${modalContent.sender_username}/${modalContent.receiver_username}`,
          {
            requestRes: false,
            notificationId: modalContent.id, // Send the notification ID
          },
          { withCredentials: true }
        );
      }

      if (response.status === 200) {
        setShowModal(false);
        setNotifications((prevNotifications) =>
          prevNotifications.map((notif) =>
            notif.id === modalContent.id ? { ...notif, is_read: true } : notif
          )
        );
        fetchNotifications();
      }
    } catch (error) {
      console.error("Error responding to notification:", error);
    }
  };

  /**
   * Close the notification modal when clicking outside of it, while the notification status stays unread.
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowModal(false); // Close the modal without marking notification as read or declining the request
    }
  };

  /**
   * Get the appropriate image object for the given type of notification
   * @param {*} type - the type of notification ("learningZoneInvite" / "palRequest")
   * @returns image object
   */
  const getNotificationImage = (type) => {
    switch (type) {
      case "learningZoneInvite":
        return learningZoneInvitation;
      case "palRequest":
        return palRequest;
      default:
        return blueBellIcon; // Fallback image
    }
  };

  return (
    <div>
      <div
        ref={notificationContainerRef}
        className={styles.notificationIcon}
        onClick={() => setShowNotifications(!showNotifications)}
      >
        <NotificationsMenu
          data={unreadNotifications.map((notification) => ({
            ...notification,
            key: notification.id.toString(),
            image: getNotificationImage(notification.type), // Set the correct image based on type
          }))}
          header={{
            title: "Notifications",
            option: {
              text: "View All",
            },
          }}
          viewAllBtn={{
            text: "View All",
          }}
          height="300px"
          width="200px"
          headerBackgroundColor="#91aec4"
          icon={blueBellIcon}
          visible={showNotifications}
        />
        {unreadNotifications.length > 0 && (
          <span className={styles.badge}>{unreadNotifications.length}</span>
        )}
      </div>

      {showModal && modalContent && (
        <ConfirmationModal
          content={modalContent}
          approveBtnText={
            modalContent.type === "palRequest" ? "Approve" : "Join"
          }
          cancelBtnText="Decline"
          show={showModal}
          onClose={handleCancel}
          onConfirm={handleConfirm}
          backdropClick={handleBackdropClick}
        />
      )}

      <ToastContainer />
    </div>
  );
};

export default NotificationHandler;
